/**
 * BrightPath - ExpenseFormPage
 * BP-04-04: Expenses Frontend
 */

import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useExpense, useCreateExpense, useUpdateExpense } from '../hooks';
import { ExpenseForm } from '../components';
import type { ExpenseCreate, ExpenseUpdate } from '../types';

export function ExpenseFormPage() {
  const { expenseId } = useParams<{ expenseId: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(expenseId);

  const { data: expense, isLoading } = useExpense(expenseId!, {
    enabled: isEditing,
  });

  const { mutateAsync: createExpense, isPending: isCreating } = useCreateExpense();
  const { mutateAsync: updateExpense, isPending: isUpdating } = useUpdateExpense();

  const handleSubmit = async (data: ExpenseCreate | ExpenseUpdate) => {
    if (isEditing && expenseId) {
      await updateExpense({ expenseId, data: data as ExpenseUpdate });
      navigate(`/family/expenses/${expenseId}`);
    } else {
      const familyId = 'current'; // Would come from auth context
      const result = await createExpense({ familyId, data: data as ExpenseCreate });
      navigate(`/family/expenses/${result.id}`);
    }
  };

  if (isEditing && isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="animate-pulse text-warm-gray">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <div className="bg-white border-b border-warm-gray/10">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-warm-gray hover:text-navy transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl border border-warm-gray/10 overflow-hidden">
          <div className="p-6 border-b border-warm-gray/10">
            <h1 className="text-2xl font-display font-semibold text-navy">
              {isEditing ? 'Edit Expense' : 'Add New Expense'}
            </h1>
            <p className="text-warm-gray mt-1">
              {isEditing
                ? 'Update the expense details below'
                : 'Track a new expense for your family'}
            </p>
          </div>

          <div className="p-6">
            <ExpenseForm
              expense={expense}
              onSubmit={handleSubmit}
              onCancel={() => navigate(-1)}
              isSubmitting={isCreating || isUpdating}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExpenseFormPage;
